import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { DotsHorizontal, TrashCanSimple, UserOutline } from "@acto/web-common/components";
import type { CustomTalkingPoint } from "@acto/taskpane/core/domain";
import { GlassButton } from "@acto/taskpane/field-sales-2/components/primitives/GlassButton";
import { MenuPopover } from "@acto/taskpane/field-sales-2/components/primitives/MenuPopover";

const Frame = styled("article")(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
  paddingTop: theme.fieldSalesTwo.spacing[24],
  paddingBottom: theme.fieldSalesTwo.spacing[24],
  borderRadius: theme.fieldSalesTwo.radius["44"],
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
  boxSizing: "border-box",
}));

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[12],
  paddingLeft: theme.fieldSalesTwo.spacing[24],
  paddingRight: theme.fieldSalesTwo.spacing[24],
}));

const ArchetypeRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
}));

const ArchetypeIcon = styled(UserOutline)(({ theme }) => ({
  flexShrink: 0,
  color: theme.fieldSalesTwo.colors.charts.primary,
}));

const ArchetypeLabel = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["15"],
  fontWeight: theme.fieldSalesTwo.font.weight["520"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const Title = styled("h2")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.familySecondary,
  fontSize: theme.fieldSalesTwo.font.size["22"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: 1.4,
  color: theme.fieldSalesTwo.colors.neutral[900],
  wordBreak: "break-word",
}));

const Actions = styled("div")(({ theme }) => ({
  display: "flex",
  paddingLeft: theme.fieldSalesTwo.spacing[24],
  paddingRight: theme.fieldSalesTwo.spacing[24],
}));

// Full-width variant of the shared glass capsule, icon-only.
const MoreCapsule = styled(GlassButton)(({ theme }) => ({
  flex: 1,
  padding: `${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[20]}`,
  color: theme.fieldSalesTwo.colors.neutral[800],
  "& svg": { width: "18px", height: "auto" },
}));

interface CustomTalkingPointCardProps {
  talkingPoint: CustomTalkingPoint;
  onDelete: () => void;
}

export function CustomTalkingPointCard({ talkingPoint, onDelete }: CustomTalkingPointCardProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.customTalkingPoint");

  return (
    <Frame>
      <Container>
        <ArchetypeRow>
          <ArchetypeIcon width={20} height={20} aria-hidden />
          <ArchetypeLabel>{t("archetype")}</ArchetypeLabel>
        </ArchetypeRow>
        <Title>{talkingPoint.title}</Title>
      </Container>
      <Actions>
        <MenuPopover
          trigger={
            <MoreCapsule type="button" aria-label={t("moreOptions")}>
              <DotsHorizontal aria-hidden="true" />
            </MoreCapsule>
          }
          align="center"
          surface="capsule"
          ariaLabel={t("moreOptions")}
          items={[
            {
              label: t("delete"),
              icon: <TrashCanSimple aria-hidden="true" />,
              tone: "destructive",
              onSelect: onDelete,
            },
          ]}
        />
      </Actions>
    </Frame>
  );
}
